// Canonical breakpoint keys, matching the `--breakpoint-*` tokens in design-system/core.
export type Breakpoint = 'base' | 'sm' | 'md' | 'lg' | 'xl';

export const BREAKPOINT_ORDER: readonly Breakpoint[] = [
  'base',
  'sm',
  'md',
  'lg',
  'xl',
];

/** A prop value that's either a single value, or a per-breakpoint override map. */
export type Responsive<T extends string> = T | Partial<Record<Breakpoint, T>>;

/**
 * Normalizes a `Responsive<T>` prop into a full breakpoint map, guaranteeing `base`
 * is always present (backfilled from `defaultValue` when the caller didn't set it).
 */
export function resolveResponsiveProp<T extends string>(
  value: Responsive<T> | undefined,
  defaultValue: T,
): Partial<Record<Breakpoint, T>> {
  if (value === undefined) return { base: defaultValue };
  if (typeof value === 'string') return { base: value };
  return value.base === undefined ? { base: defaultValue, ...value } : value;
}

/** Canonical-order entries, for iterating a resolved breakpoint map into class names. */
export function getResponsiveEntries<T extends string>(
  config: Partial<Record<Breakpoint, T>>,
): [Breakpoint, T][] {
  return BREAKPOINT_ORDER.filter((bp) => config[bp] !== undefined).map(
    (bp) => [bp, config[bp] as T] as [Breakpoint, T],
  );
}
